import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';

import apiService from '../services/api';
import { useAuth } from './AuthContext';
import Logger from '../utils/logger';

interface FeatureFlagsContextType {
  flags: Record<string, boolean>;
  isEnabled: (key: string) => boolean;
  refresh: () => Promise<void>;
  loading: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

/**
 * Loads the feature-flag map for the current user (resolving user > org > global
 * order on the backend) once at login and caches it in memory. Pages opt-in via
 * `useFeatureFlag("flag_key")`. Unknown flags resolve to `false`.
 */
export const FeatureFlagsProvider: React.FC<ProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setFlags({});
      return;
    }
    setLoading(true);
    try {
      const data = await apiService.fetchMyFeatureFlags();
      setFlags(data);
    } catch (err) {
      Logger.error('Failed to fetch feature flags', err);
      // Endpoint optional; missing flags resolve to false anyway.
      setFlags({});
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isEnabled = (key: string): boolean => Boolean(flags[key]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, isEnabled, refresh, loading }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) throw new Error('useFeatureFlags must be used inside <FeatureFlagsProvider>');
  return ctx;
};

export const useFeatureFlag = (key: string): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(key);
};
